/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
import { action } from '@ember/object';

export default class IdeController extends Controller {
  @service
  session;

  @service
  csv;

  @tracked
  validated = false;

  @tracked
  invalid = [];

  @tracked
  data = [];

  fileName = 'SMSIAM.csv';

  fileVersion = '1.71';

  @tracked
  includeStaff = false;

  @action
  setIncludeStaff(value) {
    this.includeStaff = value;
  }

  @tracked
  selectedClasses = [];

  @action
  toggleClass(entity) {
    if (this.selectedClasses.includes(entity)) {
      this.selectedClasses = this.selectedClasses.filter(
        (item) => item !== entity,
      );
    } else {
      this.selectedClasses = [...this.selectedClasses, entity];
    }
  }

  @action
  generateData() {
    let data = this.getExportData();
    let invalid = this.validateData(data);

    this.data = data;
    this.invalid = invalid;
    this.validated = true;
  }

  @action
  downloadCsv() {
    let rows = this.generateCsv(this.data);
    return this.csv.export(rows, {
      fileName: this.fileName,
      withSeparator: false,
      raw: true,
    });
  }

  @action
  selectAllClasses() {
    this.selectedClasses = this.model.homerooms.slice();
  }

  @action
  selectNone(name) {
    this.set(name, []);
  }

  @action
  startOver() {
    this.reset();
  }

  reset() {
    this.data = [];
    this.invalid = [];
    this.validated = false;
    this.includeStaff = false;
    this.selectedClasses = [];
  }

  getExportData() {
    let people = [];

    this.selectedClasses.forEach((homeroom) => {
      homeroom.currentLearners.forEach((student) => {
        if (student.person) {
          people.push(this.extractStudentData(student.person));
        }
      });

      if (this.includeStaff) {
        homeroom.currentStaff.forEach((teacher) => {
          if (teacher.person) {
            let teacherRecord = people.find(
              (item) => item.mlepSmsPersonId === teacher.person.id,
            );
            if (isEmpty(teacherRecord)) {
              people.push(
                this.extractStaffData(teacher.person, homeroom.label),
              );
            } else {
              teacherRecord.mlepHomeGroup +=
                '#' + homeroom.label.replaceAll('#', '');
              // Remove any previous quotes if exits
              teacherRecord.mlepHomeGroup =
                teacherRecord.mlepHomeGroup.replaceAll('"', '');
              teacherRecord.mlepHomeGroup = `"${teacherRecord.mlepHomeGroup}"`;
            }
          }
        });
      }
    });

    return people;
  }

  extractStudentData(student) {
    let homeRoomLabel = student.get('homeroom.label') ?? '';
    if (homeRoomLabel.includes(',')) {
      homeRoomLabel = `"${homeRoomLabel}"`;
    }
    return {
      mlepSmsPersonId: student.id,
      mlepStudentNSN: student.get('fields.nzMOE:nsn.value'),
      mlepUsername: '',
      mlepFirstAttending: student.get('fields.nzMOE:firstSchoolingDate.value'),
      mlepLastAttendance: '',
      mlepFirstName: student.firstName,
      mlepLastName: student.lastName,
      mlepPreferredName: student.get('fields.core:firstNamePreferred.value'),
      mlepRole: 'Student',
      mlepAssociatedNSN: '',
      mlepEmail: student.get('fields.core:studentEmailAddress.value'),
      mlepOrganisation: '',
      mlepGroupMembership: homeRoomLabel.replaceAll('#', ''), // hashtags are separators :|
      mlepHomeGroup: homeRoomLabel,
      mlepGender: student.gender,
      mlepDOB: student.dob,
    };
  }

  extractStaffData(teacher, homeroomLabel) {
    if (homeroomLabel.includes(',')) {
      homeroomLabel = `"${homeroomLabel}"`;
    }
    return {
      mlepSmsPersonId: teacher.id,
      mlepStudentNSN: '',
      mlepUsername: '',
      mlepFirstAttending: '',
      mlepLastAttendance: '',
      mlepFirstName: teacher.firstName,
      mlepLastName: teacher.lastName,
      mlepPreferredName: teacher.get('fields.core:firstNamePreferred.value'),
      mlepRole: 'TeachingStaff',
      mlepAssociatedNSN: '',
      mlepEmail: teacher.get('fields.core:emailAddress.value'),
      mlepOrganisation: '',
      mlepGroupMembership: '',
      mlepHomeGroup: homeroomLabel.replace('#', ''),
    };
  }

  validateData(people) {
    let invalid = [];

    people.forEach((person) => {
      let errors = {
        mlepSmsPersonId: [],
        mlepStudentNSN: [],
        mlepFirstName: [],
        mlepLastName: [],
        mlepRole: [],
        mlepFirstAttending: [],
        mlepGender: [],
        mlepDOB: [],
        person,
      };

      // Mandatory for all users.
      if (isEmpty(person.mlepSmsPersonId)) {
        errors['mlepSmsPersonId'].push('Field must not be blank.');
      }

      if (isEmpty(person.mlepFirstName)) {
        errors['mlepFirstName'].push('Field must not be blank.');
      }

      if (isEmpty(person.mlepLastName)) {
        errors['mlepLastName'].push('Field must not be blank.');
      }

      if (isEmpty(person.mlepRole)) {
        errors['mlepRole'].push('Field must not be blank.');
      }

      const validRoles = [
        'Student',
        'TeachingStaff',
        'NonTeachingStaff',
        'ParentCaregiver',
        'Alumni',
      ];

      if (!isEmpty(person.mlepRole) && !validRoles.includes(person.mlepRole)) {
        errors['mlepRole'].push('Invalid role.');
      }

      // Mandatory for students only.
      if (person.mlepRole === 'Student') {
        if (isEmpty(person.mlepStudentNSN)) {
          errors['mlepStudentNSN'].push('Field must not be blank.');
        }

        if (
          !isEmpty(person.mlepStudentNSN) &&
          !/^\d+$/.test(person.mlepStudentNSN)
        ) {
          errors['mlepStudentNSN'].push('NSN must only contain digits.');
        }

        if (
          !isEmpty(person.mlepStudentNSN) &&
          person.mlepStudentNSN.length !== 10
        ) {
          errors['mlepStudentNSN'].push('NSN must be 10 digits in length');
        }

        if (isEmpty(person.mlepFirstAttending)) {
          errors['mlepFirstAttending'].push('Field must not be blank.');
        }

        if (isEmpty(person.mlepGender)) {
          errors['mlepGender'].push('Field must not be blank.');
        }

        if (isEmpty(person.mlepDOB)) {
          errors['mlepDOB'].push('Field must not be blank.');
        }
      }

      // If there are any errors, add to error report.
      if (
        Object.values(errors).reduce(
          (hasError, column) => hasError || column.length,
          false,
        )
      ) {
        invalid.push(errors);
      }
    });

    return invalid;
  }

  generateCsv(data) {
    let rows = [];

    let fileCreated = moment().format('YYYY-MM-DD HH:mm:ss');
    let schoolJurisdictionalId = this.get('session.school.jurisdictionalId');
    let footer = [schoolJurisdictionalId, this.fileVersion, fileCreated].join(
      '#',
    );

    // Header
    rows.push([
      'mlepSmsPersonId',
      'mlepStudentNSN',
      'mlepUsername',
      'mlepFirstAttending',
      'mlepLastAttendance',
      'mlepFirstName',
      'mlepLastName',
      'mlepPreferredName',
      'mlepDOB',
      'mlepGender',
      'mlepRole',
      'mlepAssociatedNSN',
      'mlepEmail',
      'mlepOrganisation',
      'mlepGroupMembership',
      'mlepHomeGroup',
    ]);

    // Body
    let excludeIds = this.invalid.map((i) => i.person.mlepSmsPersonId);

    let filtered = data
      .filter((person) => excludeIds.indexOf(person.mlepSmsPersonId) === -1)
      .map((person) => {
        return [
          person.mlepSmsPersonId,
          person.mlepStudentNSN,
          person.mlepUsername,
          person.mlepFirstAttending,
          person.mlepLastAttendance,
          person.mlepFirstName,
          person.mlepLastName,
          person.mlepPreferredName,
          person.mlepDOB,
          person.mlepGender,
          person.mlepRole,
          person.mlepAssociatedNSN,
          person.mlepEmail,
          person.mlepOrganisation,
          person.mlepGroupMembership,
          person.mlepHomeGroup,
        ];
      });

    rows.push(...filtered);

    // Footer
    rows.push([footer]);

    return rows;
  }
}
